/* eslint-disable @eslint-react/dom/no-dangerously-set-innerhtml */
/* eslint-disable no-nested-ternary */
import { useUser } from '@folklore/auth';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { isAfter, parseISO } from 'date-fns';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'wouter';

import * as AppPropTypes from '../../lib/PropTypes';
import { getImageAtSize } from '../../lib/utils';

import { useContest } from '../../contexts/ContestContext';
import { useLabels } from '../../contexts/LabelsContext';
import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import Blocks from '../blocks/Blocks';
import Button from '../buttons/Button';
import ChevronIcon from '../icons/Chevron';
import GamesList from '../lists/Games';
import UpcomingGamesList from '../lists/UpcomingGames';
import CompleteFormEntry from '../partials/CompleteFormEntry';
import Timer from '../partials/Timer';

import styles from '../../../styles/pages/play.module.scss';

const propTypes = {
    page: AppPropTypes.page.isRequired,
    onTimerComplete: PropTypes.func,
    canPlay: PropTypes.bool,
};

const defaultProps = {
    onTimerComplete: null,
    canPlay: false,
};

function PlayPage({ page, onTimerComplete, canPlay }) {
    const route = useUrlGenerator();
    const { ended = false } = useContest() || {};
    const { name: themeName = null } = useTheme();
    const themeClassName = useThemeClassName(styles);
    const { stats = null } = useUser() || {};
    const { remainingParticipationsToday = null } = stats || {};

    const {
        games: pageGames = [],
        hiddenGames = null,
        upcomingGames: pageUpcomingGames = null,
        participations,
        maxParticipations,
        canParticipateAt = null,
        title = null,
        description = null,
        headerImage = null,
        blocks: pageBlocks = null,
    } = page || {};

    const { endedMessage = null } = useLabels();

    const games = useMemo(
        () => (pageGames || []).filter(({ type = null } = {}) => type !== 'nfl_zone'),
        [pageGames],
    );

    const upcomingGames = useMemo(
        () => (pageUpcomingGames || []).filter(({ type = null } = {}) => type !== 'nfl_zone'),
        [pageUpcomingGames],
    );

    const zones = useMemo(
        () => (pageGames || []).filter(({ type = null } = {}) => type === 'nfl_zone'),
        [pageGames],
    );
    const [zone = null] = zones || [];
    const { ended: zoneEnded = false } = zone || {};

    const blocks = useMemo(
        () =>
            pageBlocks !== null
                ? pageBlocks.map((block) => ({
                      ...block,
                      games,
                      hiddenGames,
                      upcomingGames,
                  }))
                : null,
        [pageBlocks],
    );

    const { url: imageUrl, alt: imageAlt } = getImageAtSize(headerImage, 'medium', false) || {};

    const gamesCount = (games || []).length;

    const canParticipateAtDate = useMemo(
        () => (canParticipateAt !== null ? parseISO(canParticipateAt) : null),
        [canParticipateAt],
    );

    // Always false; timer is handled in Game page
    const showTimer = useMemo(
        () => canParticipateAtDate !== null && isAfter(canParticipateAtDate, new Date()) && false,
        [canParticipateAtDate],
    );

    const canParticipate =
        (participations === null ||
            maxParticipations === null ||
            maxParticipations === -1 ||
            participations.length < maxParticipations) &&
        canPlay;

    const hasSplash = blocks !== null && blocks.length > 0 && blocks[0].type === 'splash';

    const headerBlocks =
        blocks !== null
            ? blocks.filter(({ placement = 'header' }) => (placement || 'header') === 'header')
            : null;
    const beforeBlocks =
        blocks !== null
            ? blocks.filter(({ placement = 'header' }) => placement === 'before')
            : null;
    const afterBlocks =
        blocks !== null ? blocks.filter(({ placement = 'header' }) => placement === 'after') : null;

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.hasSplash]: hasSplash,
                },
                themeClassName,
            ])}
        >
            <CompleteFormEntry className={styles.completeFormEntry} />
            {zone !== null && !ended && !zoneEnded ? (
                <Link href={zone.url} className={styles.zone}>
                    <div className={styles.zoneInner}>
                        <div className={styles.zoneLogo} />
                        <span className={styles.zoneTitle}>
                            <FormattedMessage
                                defaultMessage="Go to Lite Zone"
                                description="Button label"
                            />
                        </span>
                        <div className={styles.zoneArrow}>
                            <ChevronIcon className={styles.chevron} direction="right" />
                        </div>
                    </div>
                </Link>
            ) : null}
            {gamesCount > 0 && !ended ? (
                <>
                    {headerBlocks !== null && headerBlocks.length > 0 ? (
                        <Blocks
                            isContainer
                            className={classNames([styles.blocks, 'page-header'])}
                            containerClassName={styles.blocksInner}
                            blocks={headerBlocks}
                        />
                    ) : (
                        <div className={classNames([styles.header, 'page-header'])}>
                            {imageUrl !== null ? (
                                <img className={styles.image} src={imageUrl} alt={imageAlt} />
                            ) : null}
                            {title !== null && headerImage === null ? (
                                <h1 className={styles.title}>{title}</h1>
                            ) : null}
                            {description !== null ? (
                                <div
                                    className={styles.description}
                                    dangerouslySetInnerHTML={{ __html: description }}
                                />
                            ) : null}
                        </div>
                    )}
                    {beforeBlocks !== null && beforeBlocks.length > 0 ? (
                        <Blocks
                            isContainer
                            className={styles.blocks}
                            containerClassName={styles.blocksInner}
                            blocks={beforeBlocks}
                        />
                    ) : null}
                    {!showTimer &&
                    remainingParticipationsToday !== null &&
                    remainingParticipationsToday > 0 ? (
                        <div className={styles.remainingParticipations}>
                            <p className={styles.remainingParticipationsLabel}>
                                <FormattedMessage
                                    defaultMessage="Participations left today: <strong>{remaining}</strong>"
                                    description="Participations left message"
                                    values={{
                                        remaining: remainingParticipationsToday,
                                    }}
                                />
                            </p>
                        </div>
                    ) : null}
                    {showTimer ? (
                        <div className={styles.timerContainer}>
                            <p>
                                <FormattedMessage
                                    defaultMessage="You will be able to play in:"
                                    description="Timer description"
                                />
                            </p>
                            <Timer
                                className={styles.timer}
                                date={canParticipateAtDate}
                                started
                                onEnded={onTimerComplete}
                            />
                        </div>
                    ) : null}
                    <GamesList
                        className={styles.games}
                        items={games}
                        participations={participations}
                        canParticipate={canParticipate}
                    />
                    {afterBlocks !== null && afterBlocks.length > 0 ? (
                        <Blocks
                            isContainer
                            className={styles.blocks}
                            containerClassName={styles.blocksInner}
                            blocks={afterBlocks}
                        />
                    ) : null}
                </>
            ) : (
                <>
                    <div className={styles.noGamesContainer}>
                        {endedMessage !== null ? (
                            <div
                                className={styles.emphasis}
                                dangerouslySetInnerHTML={{ __html: endedMessage }}
                            />
                        ) : (
                            <div className={styles.noGames}>
                                <h1 className={styles.title}>
                                    {ended ? (
                                        <FormattedMessage
                                            defaultMessage="No games today{br}{br}The contest is finished."
                                            description="Games page - contest ended message"
                                        />
                                    ) : themeName === 'nfl' ? (
                                        <FormattedMessage
                                            defaultMessage="No matches today{br}{br}Come back soon{br}to play again."
                                            description="Games page - no games today message"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="No games today{br}{br}Come back soon{br}to play again."
                                            description="Games page - no games today message"
                                        />
                                    )}
                                </h1>
                                <Button href={route('account')} className={styles.button}>
                                    <FormattedMessage
                                        defaultMessage="Go to my account"
                                        description="Button label"
                                    />
                                </Button>
                            </div>
                        )}
                    </div>
                    {upcomingGames !== null && upcomingGames.length > 0 ? (
                        <div className={styles.upcomingGamesContainer}>
                            <h4 className={styles.title}>
                                <FormattedMessage
                                    defaultMessage="Upcoming games"
                                    description="List label"
                                />
                            </h4>
                            <UpcomingGamesList
                                className={styles.upcomingGames}
                                items={upcomingGames}
                            />
                        </div>
                    ) : null}
                </>
            )}
        </div>
    );
}

PlayPage.propTypes = propTypes;
PlayPage.defaultProps = defaultProps;

export default PlayPage;
